import { useValue } from "@legendapp/state/react";
import {
  MAX_MENTIONS_PER_MESSAGE,
  MENTION_KINDS,
  MENTION_PART_TYPE,
  type MentionKind,
  type MentionPartData,
} from "@repo/shared/types/mention-part";
import { nanoid } from "@repo/shared/utils/id";
import { Extension, type JSONContent } from "@tiptap/core";
import Mention from "@tiptap/extension-mention";
import Placeholder from "@tiptap/extension-placeholder";
import { PluginKey } from "@tiptap/pm/state";
import {
  EditorContent,
  type NodeViewProps,
  NodeViewWrapper,
  ReactNodeViewRenderer,
  ReactRenderer,
  useEditor,
} from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import {
  exitSuggestion,
  type SuggestionKeyDownProps,
  type SuggestionProps,
} from "@tiptap/suggestion";
import type { UIMessage } from "ai";
import {
  createRef,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
} from "react";

import { blocks$ } from "~/stores/blocks/blocks.crud";
import { boards$ } from "~/stores/boards/boards.crud";
import { datasets$ } from "~/stores/datasets/datasets.crud";
import { datasources$ } from "~/stores/datasources/datasources.crud";
import { useMentionAction } from "./mention-actions";
import { MentionPillIcon } from "./mention-icon";
import { MentionPopover, type MentionPopoverHandle } from "./mention-popover";
import type { MentionItem } from "./mention-search";

export type ChatInputEditorProps = {
  placeholder?: string;
  disabled?: boolean;
  /**
   * Called whenever the editor content changes. `text` is the rendered plain
   * text (mentions serialized as `@<label>`); `mentionCount` reflects the
   * current number of mention nodes in the doc.
   */
  onChange?: (state: { text: string; mentionCount: number }) => void;
  onSubmit?: () => void;
  /** Called when the user pastes image files into the editor. */
  onPasteFiles?: (files: File[]) => void;
};

export type ChatInputEditorHandle = {
  focus: () => void;
  clear: () => void;
  /** Plain text with mentions inlined as `@<label>`. */
  getText: () => string;
  /**
   * Walks the editor's JSON document and returns one entry per mention node.
   * Duplicates are preserved (the agent sees them; the server-side processor
   * dedupes by entityId+kind before resolving).
   */
  getMentions: () => MentionPartData[];
  /**
   * Walks the editor's JSON document in document order and returns the
   * UIMessage parts to send: alternating `text` segments and `data-mention`
   * parts at the position the pill appeared. Paragraph breaks become `\n`
   * inside text runs. Empty/whitespace-only text segments are dropped, and
   * mentions beyond {@link MAX_MENTIONS_PER_MESSAGE} are silently truncated.
   */
  getMessageParts: () => UIMessage["parts"];
  isEmpty: () => boolean;
};

/** Mention attrs we control on the inserted node. */
type MentionAttrs = {
  id: string;
  label: string;
  kind: MentionPartData["kind"];
};

/** Type guard against the shared `MENTION_KINDS` array so adding a new kind upstream automatically widens this check. */
const isMentionKind = (value: unknown): value is MentionKind =>
  typeof value === "string" &&
  (MENTION_KINDS as readonly string[]).includes(value);

/**
 * Recursively walks the editor JSON for mention nodes. Tiptap stores them as
 * `{ type: "mention", attrs: { id, label, kind } }` — see the extended Mention
 * config below.
 */
const collectMentions = (
  doc: JSONContent | undefined,
  out: MentionPartData[],
): void => {
  if (!doc) return;
  if (doc.type === "mention" && doc.attrs) {
    const { id, label, kind } = doc.attrs as Partial<MentionAttrs>;
    if (
      typeof id === "string" &&
      typeof label === "string" &&
      isMentionKind(kind)
    ) {
      out.push({ kind, entityId: id, label } as MentionPartData);
    }
  }
  if (doc.content) {
    for (const child of doc.content) collectMentions(child, out);
  }
};

/**
 * Walks the editor JSON in document order and emits ordered UIMessage parts.
 * Text runs are coalesced; consecutive text inside a single paragraph is
 * concatenated, and paragraph boundaries inject a `\n` into the active text
 * buffer. Mention nodes flush the buffer and emit a `data-mention` part at
 * their position. Hard-breaks (StarterKit `hardBreak`) also inject `\n`.
 */
const walkParts = (
  doc: JSONContent | undefined,
  state: { text: string; parts: UIMessage["parts"]; mentions: number },
): void => {
  if (!doc) return;

  if (doc.type === "text" && typeof doc.text === "string") {
    state.text += doc.text;
    return;
  }

  if (doc.type === "hardBreak") {
    state.text += "\n";
    return;
  }

  if (doc.type === "mention" && doc.attrs) {
    const { id, label, kind } = doc.attrs as Partial<MentionAttrs>;
    if (
      typeof id === "string" &&
      typeof label === "string" &&
      isMentionKind(kind) &&
      state.mentions < MAX_MENTIONS_PER_MESSAGE
    ) {
      if (state.text.length > 0) {
        state.parts.push({ type: "text", text: state.text });
        state.text = "";
      }
      state.parts.push({
        type: MENTION_PART_TYPE,
        id: nanoid(),
        data: { kind, entityId: id, label } as MentionPartData,
      });
      state.mentions++;
    }
    return;
  }

  if (doc.content) {
    for (const child of doc.content) walkParts(child, state);
  }

  if (doc.type === "paragraph") {
    state.text += "\n";
  }
};

/**
 * React NodeView for the mention atom. Tiptap drives mounting/unmounting as
 * the document mutates; we render a real React tree so the pill can pull live
 * data from Legend State stores instead of baking a static SVG into
 * `renderHTML`.
 */
const MentionNodeView = ({ node }: NodeViewProps) => {
  const id = node.attrs.id as string;
  const label = (node.attrs.label as string) ?? id;
  const kind = node.attrs.kind as MentionPartData["kind"];
  return (
    <NodeViewWrapper
      as="span"
      className="chat-mention"
      data-id={id}
      data-label={label}
      data-kind={kind}
      data-type="mention"
      title={label}
    >
      <MentionPillIcon id={id} kind={kind} />
      <span className="chat-mention-label">{label}</span>
    </NodeViewWrapper>
  );
};

/**
 * Stable plugin key for the `@`-mention suggestion plugin. We pass this to
 * Tiptap's Mention `suggestion.pluginKey` so we can dispatch
 * `exitSuggestion(view, mentionPluginKey)` from the popover when the user
 * clicks / presses outside. Without an override Tiptap creates an anonymous
 * key per editor instance, leaving no programmatic way to dismiss.
 */
const mentionPluginKey = new PluginKey("chatMention");

/** Extends the default Mention node with our `kind` attribute and React NodeView. */
const KindAwareMention = Mention.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      kind: {
        default: null,
        parseHTML: (el) => el.getAttribute("data-kind"),
        renderHTML: (attrs) => (attrs.kind ? { "data-kind": attrs.kind } : {}),
      },
    };
  },
  addNodeView() {
    return ReactNodeViewRenderer(MentionNodeView);
  },
});

/**
 * Tiptap-based chat input. Replaces the plain textarea so we can render inline
 * mention pills and walk the JSON doc for structured mention data on submit.
 *
 * Tiptap owns: document model, caret, mention trigger lifecycle.
 * This component owns: imperative API consumed by the chat container, the
 * `MentionPopover` mounted via `ReactRenderer`, and Enter→submit semantics.
 *
 * Enter behavior is delivered via the `SubmitOnEnter` extension below
 * (registered as a ProseMirror keymap plugin) so Mention's higher-priority
 * Suggestion plugin can claim Enter first when the popover is active:
 * - Popover open with items: Mention commits selection, submit never fires.
 * - Popover closed or empty: SubmitOnEnter fires `onSubmit`.
 * - Shift+Enter: handled by StarterKit's HardBreak (inserts a newline).
 */
export const ChatInputEditor = forwardRef<
  ChatInputEditorHandle,
  ChatInputEditorProps
>(({ placeholder, disabled, onChange, onSubmit, onPasteFiles }, ref) => {
  const onSubmitRef = useRef(onSubmit);
  onSubmitRef.current = onSubmit;
  const onPasteFilesRef = useRef(onPasteFiles);
  onPasteFilesRef.current = onPasteFiles;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const extensions = useMemo(() => {
    /**
     * Submit-on-Enter as a Tiptap extension (`addKeyboardShortcuts` registers
     * a ProseMirror keymap plugin in `state.plugins`). Mention has
     * `priority: 101` so its Suggestion plugin is registered first and gets
     * the keystroke first when the popover is active. Only when Mention's
     * `handleKeyDown` returns `false` (popover closed or empty) does this
     * keymap run and submit.
     *
     * Keep this on `editorProps.handleKeyDown` and ProseMirror checks
     * `view._props.handleKeyDown` BEFORE any plugin — Mention never sees
     * Enter and submit always wins. See `view.someProp` in `prosemirror-view`.
     */
    const SubmitOnEnter = Extension.create({
      name: "submitOnEnter",
      addKeyboardShortcuts() {
        return {
          Enter: () => {
            onSubmitRef.current?.();
            return true;
          },
        };
      },
    });

    return [
      StarterKit.configure({
        // Keep: document, paragraph, text, hardBreak, undoRedo, trailingNode.
        // Drop everything else — chat is plain text only.
        blockquote: false,
        bold: false,
        bulletList: false,
        code: false,
        codeBlock: false,
        dropcursor: false,
        heading: false,
        horizontalRule: false,
        italic: false,
        link: false,
        listItem: false,
        listKeymap: false,
        orderedList: false,
        strike: false,
        underline: false,
      }),
      Placeholder.configure({
        placeholder: placeholder ?? "",
        emptyEditorClass: "is-editor-empty",
      }),
      KindAwareMention.configure({
        HTMLAttributes: { class: "chat-mention" },
        deleteTriggerWithBackspace: true,
        renderText: ({ node }) => `@${node.attrs.label ?? node.attrs.id}`,
        // HTML serialization fallback (used by `editor.getHTML()` / clipboard
        // copy). The visual pill is owned by `MentionNodeView`, so we just
        // emit data attributes + label here — no leading `@`, no icon.
        renderHTML: ({ node, options }) => [
          "span",
          {
            ...options.HTMLAttributes,
            "data-id": node.attrs.id,
            "data-label": node.attrs.label,
            "data-kind": node.attrs.kind,
            "data-type": "mention",
          },
          node.attrs.label ?? node.attrs.id,
        ],
        suggestion: {
          char: "@",
          pluginKey: mentionPluginKey,
          allowSpaces: false,
          // Default `allowedPrefixes: [' ']` already prevents `user@gmail.com`
          // from triggering the popover (must be preceded by whitespace or
          // start of doc).
          items: ({ query }) => {
            // The popover does its own filtering off Legend State stores; we
            // just forward `query` so Tiptap re-renders on every keystroke.
            // Returning a non-empty stub keeps the suggestion plugin "active"
            // (otherwise some versions exit early on empty-items).
            void query;
            return [];
          },
          render: () => {
            // We own the popover ref directly instead of relying on
            // `renderer.ref`. ReactRenderer's callback-ref injection is flaky
            // under React 19 + forwardRef (see ueberdosis/tiptap#6110), and
            // when `ref` is a plain prop the popover's `useImperativeHandle`
            // populates `.current` deterministically.
            let renderer: ReactRenderer<MentionPopoverHandle> | null = null;
            let popoverRef = createRef<MentionPopoverHandle>();

            const buildProps = (props: SuggestionProps<MentionItem>) => ({
              ref: popoverRef,
              query: props.query,
              clientRect: props.clientRect,
              onSelect: (item: MentionItem) => {
                // Tiptap's typed `command` is keyed off the default
                // MentionNodeAttrs and doesn't know about our extended `kind`
                // attribute (added via `addAttributes` on the node). Cast
                // through to the underlying attrs shape — Mention's command
                // implementation feeds these straight into `node.attrs`.
                (props.command as unknown as (attrs: MentionAttrs) => void)({
                  id: item.id,
                  label: item.label,
                  kind: item.kind,
                });
              },
              // Called when Base UI signals a dismiss intent (outside press,
              // escape, focus-out). Exits the suggestion via the shared plugin
              // key so the plugin records `dismissedRange` and won't reopen
              // until the user types past the trigger or starts a fresh `@`.
              onDismiss: () => {
                exitSuggestion(props.editor.view, mentionPluginKey);
              },
            });

            return {
              onStart: (props: SuggestionProps<MentionItem>) => {
                // Fresh ref per `@` session so successive triggers don't
                // share state.
                popoverRef = createRef<MentionPopoverHandle>();
                renderer = new ReactRenderer(MentionPopover, {
                  props: buildProps(props),
                  editor: props.editor,
                });
              },
              onUpdate: (props: SuggestionProps<MentionItem>) => {
                renderer?.updateProps(buildProps(props));
              },
              onKeyDown: (props: SuggestionKeyDownProps): boolean => {
                if (props.event.key === "Escape") {
                  renderer?.destroy();
                  renderer = null;
                  return true;
                }
                if (popoverRef.current) {
                  return popoverRef.current.onKeyDown(props.event);
                }
                // Popover hasn't hydrated yet (suggestion opened this tick).
                // Swallow commit keys so `@` + Enter doesn't fall through to
                // SubmitOnEnter and send the prompt before the user can pick.
                if (props.event.key === "Enter" || props.event.key === "Tab") {
                  return true;
                }
                return false;
              },
              onExit: () => {
                renderer?.destroy();
                renderer = null;
              },
            };
          },
        },
      }),
      SubmitOnEnter,
    ];
  }, [placeholder]);

  const editor = useEditor({
    extensions,
    editable: !disabled,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class:
          "chat-editor min-h-16 w-full resize-none bg-transparent px-2 py-2 text-left text-xs/relaxed outline-hidden focus:outline-hidden",
        "data-slot": "input-group-control",
        role: "textbox",
        "aria-multiline": "true",
      },
      handlePaste(_view, event) {
        const items = event.clipboardData?.items;
        if (!items) return false;
        const files: File[] = [];
        for (const item of items) {
          if (item.kind === "file" && item.type.startsWith("image/")) {
            const file = item.getAsFile();
            if (file) files.push(file);
          }
        }
        if (files.length > 0) {
          event.preventDefault();
          onPasteFilesRef.current?.(files);
          return true;
        }
        return false;
      },
      // Mirror handlePaste for native drag-and-drop. Without this, dropping an
      // image onto the contenteditable would trigger the browser default
      // (insert as an <img> or navigate to the file URL).
      handleDrop(_view, event) {
        const dt = (event as DragEvent).dataTransfer;
        const files = dt ? Array.from(dt.files) : [];
        const imageFiles = files.filter((f) => f.type.startsWith("image/"));
        if (imageFiles.length > 0) {
          event.preventDefault();
          onPasteFilesRef.current?.(imageFiles);
          return true;
        }
        return false;
      },
      // Strip mention nodes when pasting Tiptap HTML between editor instances:
      // accept the plain `@Label` text but never recreate a structured pill,
      // since the source IDs may not match the destination org.
      transformPastedHTML(html) {
        return html.replace(
          /<span[^>]*data-type="mention"[^>]*>([^<]*)<\/span>/g,
          "$1",
        );
      },
    },
    onUpdate({ editor }) {
      const text = editor.getText();
      const json = editor.getJSON();
      const list: MentionPartData[] = [];
      collectMentions(json, list);
      onChangeRef.current?.({ text, mentionCount: list.length });
    },
  });

  useEffect(() => {
    if (editor) editor.setEditable(!disabled);
  }, [editor, disabled]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => editor?.commands.focus(),
      clear: () => {
        editor?.commands.clearContent(true);
      },
      getText: () => editor?.getText() ?? "",
      getMentions: () => {
        if (!editor) return [];
        const out: MentionPartData[] = [];
        collectMentions(editor.getJSON(), out);
        return out.slice(0, MAX_MENTIONS_PER_MESSAGE);
      },
      getMessageParts: () => {
        if (!editor) return [];
        const state = {
          text: "",
          parts: [] as UIMessage["parts"],
          mentions: 0,
        };
        walkParts(editor.getJSON(), state);
        const trailing = state.text.replace(/\n+$/, "");
        if (trailing.length > 0) {
          state.parts.push({ type: "text", text: trailing });
        }
        return state.parts;
      },
      isEmpty: () => editor?.isEmpty ?? true,
    }),
    [editor],
  );

  return (
    <EditorContent
      editor={editor}
      className="no-scrollbar flex min-h-0 w-full flex-1 overflow-y-auto"
    />
  );
});

ChatInputEditor.displayName = "ChatInputEditor";

/**
 * Returns the best available display name for a mention chip. Prefers the
 * live entity name from the local store (so renames and AI-emitted placeholder
 * labels are self-healed), falls back to the persisted `label` prop, and
 * finally the raw `id` as a last resort.
 *
 * All four `useValue` calls are unconditional to satisfy React's rules of
 * hooks; the `kind` discriminator picks which value is actually used.
 */
const useLiveChipLabel = (
  id: string,
  kind: MentionPartData["kind"],
  label: string,
): string => {
  const blockName = useValue(blocks$[id]?.name);
  const boardName = useValue(boards$[id]?.name);
  const datasetName = useValue(datasets$[id]?.name);
  const datasourceName = useValue(datasources$[id]?.name);

  const liveName =
    kind === "block"
      ? blockName
      : kind === "board"
        ? boardName
        : kind === "dataset"
          ? datasetName
          : datasourceName;

  return liveName?.trim() || label || id;
};

/**
 * Presentational chip used by every "mention pill" surface (past-message
 * chips, ambient context chips above the input). Renders icon + label with
 * the shared `chat-mention` class so all surfaces stay visually identical.
 *
 * The display name is resolved from the live local store so renamed entities
 * and AI-emitted placeholder labels (e.g. `[blocked]`) are self-healed.
 */
export const MentionChipDisplay = ({
  id,
  label,
  kind,
}: {
  id: string;
  label: string;
  kind: MentionPartData["kind"];
}) => {
  const displayLabel = useLiveChipLabel(id, kind, label);
  return (
    <span
      title={displayLabel}
      className="chat-mention inline-flex items-center gap-1 rounded-[calc(var(--radius-sm)-2px)] bg-muted-foreground/10 px-1 font-medium text-foreground text-xs/relaxed"
    >
      <MentionPillIcon id={id} kind={kind} />
      <span className="chat-mention-label">{displayLabel}</span>
    </span>
  );
};

/**
 * Decorative chip used to render mention parts in past message bubbles. Reads
 * the same live entity data as the editor's NodeView so a renamed/re-iconed
 * entity reflects in history without needing to round-trip through the agent.
 *
 * The display name is resolved from the live local store so renamed entities
 * and AI-emitted placeholder labels (e.g. `[blocked]`) are self-healed.
 */
export const MentionChip = ({
  id,
  label,
  kind,
}: {
  id: string;
  label: string;
  kind: MentionPartData["kind"];
}) => {
  const onAction = useMentionAction();
  const displayLabel = useLiveChipLabel(id, kind, label);
  return (
    <button
      type="button"
      onClick={() => onAction(kind, id)}
      title={displayLabel}
      className="chat-mention inline-flex cursor-pointer items-center gap-1 rounded-[calc(var(--radius-sm)-2px)] bg-muted-foreground/10 px-1 font-medium text-foreground text-xs/relaxed hover:bg-muted-foreground/20"
    >
      <MentionPillIcon id={id} kind={kind} />
      <span className="chat-mention-label">{displayLabel}</span>
    </button>
  );
};
